/**
 * Browser Notification Utility
 * Handles notification permissions, display, and badge updates
 */
import formationLogo from '@/assets/formation-logo.png';
import appIcon from '@/assets/app-icon-512.png';

export type NotificationPermissionStatus = 'granted' | 'denied' | 'default';

interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
}

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermissionStatus> => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported in this browser');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission as NotificationPermissionStatus;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Check if notifications are supported and enabled
 */
export const areNotificationsEnabled = (): boolean => {
  return 'Notification' in window && Notification.permission === 'granted';
};

/**
 * Show a browser notification
 */
export const showNotification = async (options: NotificationOptions): Promise<void> => {
  if (!areNotificationsEnabled()) {
    console.warn('Notifications not enabled');
    return;
  }

  // Don't show if window is focused
  if (document.hasFocus()) {
    console.log('Window is focused, skipping notification');
    return;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || formationLogo,
      badge: options.badge || appIcon,
      tag: options.tag,
      data: options.data,
      requireInteraction: options.requireInteraction || false,
    });

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      
      // Navigate to the appropriate section if data is provided
      if (options.data?.redirectTo) {
        window.location.href = options.data.redirectTo;
      }
      
      notification.close();
    };

    // Auto-close after 10 seconds if not set to require interaction
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 10000);
    }
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

/**
 * Update the app badge count (PWA feature)
 */
export const updateBadgeCount = async (count: number): Promise<void> => {
  if (!('setAppBadge' in navigator)) {
    // Badge API is only available in some browsers/PWAs; noop on unsupported environments (including native)
    return;
  }

  try {
    if (count > 0) {
      await (navigator as any).setAppBadge(count);
    } else {
      await (navigator as any).clearAppBadge();
    }
  } catch (error) {
    console.error('Error updating badge:', error);
  }
};

/**
 * Clear the app badge
 */
export const clearBadge = async (): Promise<void> => {
  await updateBadgeCount(0);
};

/**
 * Clear all delivered push notifications from the device notification tray
 * Works on both iOS and Android via Capacitor
 */
export const clearAllDeliveredNotifications = async (): Promise<void> => {
  try {
    // Check if we're on a native platform
    const { Capacitor } = await import('@capacitor/core');
    
    if (Capacitor.isNativePlatform()) {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      
      // Remove all delivered notifications from the notification center
      await PushNotifications.removeAllDeliveredNotifications();
      
      console.log('✅ Cleared all delivered push notifications');
    }
    
    // Also clear web badge
    await clearBadge();
  } catch (error) {
    console.error('Error clearing delivered notifications:', error);
  }
};

/**
 * Check if user prefers to receive notifications
 */
export const getNotificationPreference = (): boolean => {
  const preference = localStorage.getItem('notifications_enabled');
  return preference !== 'false'; // Default to true
};

/**
 * Set user notification preference
 */
export const setNotificationPreference = (enabled: boolean): void => {
  localStorage.setItem('notifications_enabled', enabled.toString());
};

/**
 * Notification type helpers
 */
export const NotificationTypes = {
  TASK_INVITATION: 'task_invitation',
  VERIFICATION_REQUEST: 'verification_request',
  FRIEND_REQUEST: 'friend_request',
  ENGAGEMENT_REMINDER: 'engagement_reminder',
  ADMIN_ANNOUNCEMENT: 'admin_announcement',
  ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
} as const;

/**
 * Create notification options for different types
 */
export const createNotificationOptions = (
  type: string,
  data: any
): NotificationOptions => {
  switch (type) {
    case NotificationTypes.TASK_INVITATION:
      return {
        title: 'New Task Invitation',
        body: `${data.inviterName} invited you to complete "${data.taskTitle}" together`,
        tag: `task-invitation-${data.invitationId}`,
        data: {
          type,
          id: data.invitationId,
          redirectTo: '/app?tab=notifications&subtab=invitations',
        },
      };

    case NotificationTypes.VERIFICATION_REQUEST:
      return {
        title: 'Verification Request',
        body: `${data.requesterName} needs you to verify "${data.taskTitle}"`,
        tag: `verification-${data.requestId}`,
        data: {
          type,
          id: data.requestId,
          redirectTo: '/app?tab=notifications&subtab=verifications',
        },
      };

    case NotificationTypes.ENGAGEMENT_REMINDER:
      return {
        title: 'Time to Grow!',
        body: `You haven't completed any ${data.areaName} tasks recently. Let's keep the momentum going!`,
        tag: `reminder-${data.areaId}`,
        data: {
          type,
          areaId: data.areaId,
          redirectTo: `/app?tab=journey&area=${data.areaId}`,
        },
      };

    case NotificationTypes.ADMIN_ANNOUNCEMENT:
      return {
        title: data.title,
        body: data.message,
        tag: `announcement-${data.notificationId}`,
        requireInteraction: true,
        data: {
          type,
          id: data.notificationId,
          redirectTo: '/app?tab=notifications&subtab=announcements',
        },
      };

    default:
      return {
        title: 'New Notification',
        body: data.message || 'You have a new notification',
        tag: `notification-${Date.now()}`,
        data: { type, ...data },
      };
  }
};
